import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { useEnableGcpAccount } from "featureFlags/hooks/useEnableGcpAccount";
import { getDatabaseDefinitions } from "services/databases/createWorkspaceDatabase";
import { isSystemDatabase } from "services/databases/utils";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";

import DatabaseIcon from "assets/icons/workspace/Database.svg?react";
import SystemDatabaseIcon from "assets/icons/workspace/SystemDatabase.svg?react";

import useActiveEditorView from "pages/DevelopWorkspace/contexts/ActiveEditorViewContext/hooks/useActiveEditorView";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";

import { useCurrentAccount } from "components/Account/useCurrentAccount";
import { useMenu } from "components/ActionMenu/useMenu";
import { useAccessManager } from "components/App/accessManager";
import DefinitionCodePreview from "components/DefinitionCodePreview/DefinitionCodePreview";
import { PinIconEmpty, PinIconFill } from "components/Icons";
import { IngestionStep } from "components/IngestionWizard/types";
import { RecordLabelWithIcon } from "components/LeftSidebar/RecordLabelWithIcon";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";
import { TreeNodeType } from "components/Tree/types";
import { highlightText } from "components/helpers";

import {
  RecordMenuItemGroup,
  RecordMenuItemSubGroup,
} from "../../RecordMenu/types";
import { useLeftSidebarContext } from "../../context";
import { DatabaseMenuActions } from "../types";

import sidebarStyles from "../../styles.module.scss";
import styles from "./styles.module.scss";

type Props = {
  node: TreeNodeType;
  actions: DatabaseMenuActions;
};

export const DatabaseLabel = (props: Props) => {
  const { node, actions } = props;
  const {
    deleteDatabase,
    openLoadData,
    openEditDatabase,
    isPinned,
    onTogglePin,
    showPin,
  } = actions;
  const {
    state: { activeDocumentId },
    actions: { changeDocumentContext },
  } = useDocuments();
  const { label, value, payload } = node;
  const menu = useMenu();
  const { t } = useTranslation();
  const { isAllowedTo } = useAccessManager();
  const enabledGcpAccount = useEnableGcpAccount();
  const { insertTextAtCursorPosition } = useActiveEditorView();
  const { getAccount } = useCurrentAccount();
  const account = getAccount();
  const { databaseSearchState } = useLeftSidebarContext();

  const canModifyDatabase =
    isAllowedTo(PrivilegeObjectType.database, value, SqlRbacAction.MODIFY, {
      owner: payload.catalogOwner,
    }) && !enabledGcpAccount;

  const canCreateDatabase =
    isAllowedTo(
      PrivilegeObjectType.account,
      account?.accountName,
      SqlRbacAction.DATABASE_CREATE
    ) && !enabledGcpAccount;

  const generateSecondaryItems = (): RecordMenuItemGroup => {
    const items: RecordMenuItemSubGroup = [];

    if (canModifyDatabase) {
      items.push({
        key: "edit-database",
        text: t("workspace.database_menu.edit_database"),
        action: () => {
          openEditDatabase(payload);
        },
        testId: "edit-db-item",
      });
    }

    if (canCreateDatabase) {
      items.push({
        key: "load-wizard-with-db",
        text: t("workspace.database_menu.load_data"),
        action: () => {
          openLoadData({
            [IngestionStep.selectDestination]: {
              type: null,
              database: value,
            },
          });
        },
        testId: "load-wizard-with-db-item",
      });
    }

    if (canModifyDatabase) {
      items.push({
        key: "delete",
        text: t("workspace.database_menu.delete"),
        action: () => {
          deleteDatabase(value);
        },
        testId: "delete-db-item",
      });
    }

    return items.length ? [items] : [];
  };

  const generalItems = [
    {
      key: "context",
      text: t("workspace.database_menu.context"),
      action: () => {
        if (activeDocumentId) {
          changeDocumentContext(activeDocumentId, {
            database: { name: value, isUserSelected: true },
          });
        }
      },
      testId: "set-db-document-context-item",
    },
    {
      key: "insert",
      text: t("workspace.database_menu.insert"),
      shortcut: "Shift + Click",
      action: () => {
        if (activeDocumentId) {
          insertTextAtCursorPosition(value);
        }
      },
      testId: "insert-db-into-editor-item",
    },
  ];

  const databaseItems = [
    [
      ...generalItems,
      {
        key: "view",
        text: "View database definition",
        renderContent: (props: { onContentClose: () => void }) => {
          const code = getDatabaseDefinitions({
            catalogName: value,
            description: payload.description,
          });
          return (
            <DefinitionCodePreview
              code={code}
              onSecondaryButtonClick={() => {
                if (activeDocumentId) {
                  insertTextAtCursorPosition(`\n${code}`);
                }
                menu.closeMenu();
                props.onContentClose();
              }}
            />
          );
        },
        testId: "view-db-definition-item",
      },
    ],
    ...generateSecondaryItems(),
  ];

  const isSystem = isSystemDatabase(value);
  const pinned = isPinned?.(value) ?? false;
  const canShowPin = Boolean(showPin && onTogglePin && !isSystem);
  const items = isSystem ? [generalItems] : databaseItems;

  const stopPropagation = (event: React.SyntheticEvent) =>
    event.stopPropagation();

  return (
    <>
      <RecordLabelWithIcon
        icon={isSystem ? <SystemDatabaseIcon /> : <DatabaseIcon />}
        htmlTitle={label}
        label={
          <span className={styles.databaseLabel}>
            {highlightText({
              searchWord: databaseSearchState,
              textToHighlight: label,
            })}
          </span>
        }
        suffix={
          canShowPin ? (
            <span
              className={classNames(sidebarStyles.pin, styles.pinButton, {
                [styles.pinButtonPinned]: pinned,
              })}
              onMouseOver={stopPropagation}
              onClick={event => {
                stopPropagation(event);
                onTogglePin?.(value);
              }}
            >
              {pinned ? <PinIconFill /> : <PinIconEmpty />}
            </span>
          ) : null
        }
        testId={`db-label-${label}`}
      />
      <Menu
        testId="database-more-icon"
        items={items}
        menu={menu}
      />
    </>
  );
};
